// =============================================================================
// Resource Types Tab - Gestione Tipi di Risorsa con Schema Builder
// =============================================================================

import { useState } from 'react';
import { Plus, Pencil, Trash2, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useResourceTypes } from '../../api/hooks';
import { ResourceType } from '../../types';
import { ResourceTypeFormModal } from './ResourceTypeFormModal';
import { ResourceTypeDeleteDialog } from './ResourceTypeDeleteDialog';
import {
  Badge,
  Button,
  EmptyState,
  ErrorBlock,
  IconButton,
  LoadingBlock,
  SettingsCard,
  TabPageHeader,
} from './_shared';
import { resolveResourceTypeIcon } from '../../lib/resourceTypeIcons';

export function ResourceTypesTab() {
  const { data: resourceTypes = [], isLoading, error } = useResourceTypes();
  const { t } = useTranslation('settings');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ResourceType | null>(null);
  const [typeToDelete, setTypeToDelete] = useState<ResourceType | null>(null);

  const handleEdit = (type: ResourceType) => {
    setSelectedType(type);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedType(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedType(null);
  };

  if (isLoading) {
    return <LoadingBlock />;
  }

  if (error) {
    return <ErrorBlock message={t('resourceTypes.loadError')} />;
  }

  return (
    <div>
      <TabPageHeader
        title={t('resourceTypes.title')}
        description={t('resourceTypes.subtitle')}
        action={
          <Button icon={Plus} onClick={handleCreate}>
            {t('resourceTypes.newType')}
          </Button>
        }
      />

      {resourceTypes.length === 0 ? (
        <SettingsCard>
          <EmptyState
            icon={Layers}
            title={t('resourceTypes.emptyTitle')}
            description={t('resourceTypes.emptyMessage')}
            action={
              <Button icon={Plus} onClick={handleCreate}>
                {t('resourceTypes.createFirst')}
              </Button>
            }
          />
        </SettingsCard>
      ) : (
        <div className="grid gap-4">
          {resourceTypes.map((type) => {
            // `type.icon` è un nome di icona lucide: va risolto nel componente,
            // altrimenti si ricade sull'iniziale del nome del tipo.
            const TypeIcon = resolveResourceTypeIcon(type.icon);
            return (
              <SettingsCard key={type.id}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    {/* Icona/Colore */}
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl font-semibold flex-shrink-0 shadow-sm"
                      style={{ backgroundColor: type.color || '#6B7280' }}
                    >
                      {TypeIcon ? (
                        <TypeIcon className="w-6 h-6" strokeWidth={2} />
                      ) : (
                        type.name.charAt(0).toUpperCase()
                      )}
                    </div>

                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-gray-900">{type.name}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {t('resourceTypes.codeLabel')}{' '}
                        <code className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-xs font-mono">
                          {type.code}
                        </code>
                      </p>
                      {type.description && (
                        <p className="text-sm text-gray-600 mt-2">{type.description}</p>
                      )}

                      {type.attributesSchema &&
                        Array.isArray(type.attributesSchema) &&
                        type.attributesSchema.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                              {t('resourceTypes.customAttributes', {
                                count: type.attributesSchema.length,
                              })}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {type.attributesSchema.map((attr) => (
                                <Badge key={attr.key} tone="gray">
                                  <span className="font-semibold">{attr.label}</span>
                                  <span className="text-gray-400 ml-1">({attr.type})</span>
                                  {attr.required && <span className="text-red-500 ml-0.5">*</span>}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Azioni */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <IconButton
                      icon={Pencil}
                      tone="primary"
                      label={t('common:buttons.edit')}
                      onClick={() => handleEdit(type)}
                    />

                    <IconButton
                      icon={Trash2}
                      tone="danger"
                      label={t('common:buttons.delete')}
                      onClick={() => setTypeToDelete(type)}
                    />
                  </div>
                </div>
              </SettingsCard>
            );
          })}
        </div>
      )}

      {/* Modal Form */}
      {isModalOpen && (
        <ResourceTypeFormModal
          resourceType={selectedType}
          onClose={handleCloseModal}
        />
      )}

      {/* Conferma eliminazione (con controllo dipendenze) */}
      {typeToDelete && (
        <ResourceTypeDeleteDialog
          resourceType={typeToDelete}
          onClose={() => setTypeToDelete(null)}
        />
      )}
    </div>
  );
}
